'use client';

import AppBannerComponent, { type AppBannerProps as AppBannerComponentProps } from '@pixum/app-banner';
import clsx from 'clsx';
import { CREATIVE_ID, useAppAdjustLink } from 'hooks';
import { useEffect, useState } from 'react';
import { trackLinkOut } from '../../utils/analytics';
import { type LinkProps, withLinkMasking, type WrappedComponent } from '../link';
import styles from './AppBanner.module.css';
import type { AppBannerProps } from './types';
import { shouldShow, dismiss } from './utils';

const AppBannerWithLinkMasking = withLinkMasking<LinkProps & AppBannerComponentProps>(
  AppBannerComponent as WrappedComponent<AppBannerComponentProps>,
);

/**
 * AppBanner component that displays a promotional banner for the mobile app with smart visibility management.
 *
 * This component wraps the external @pixum/app-banner component with additional functionality including:
 * - Link masking for SEO purposes
 * - App link adjustment via Adjust tracking
 * - Persistent visibility state stored in localStorage
 * - Delayed display (5 seconds after mount) to avoid interrupting initial user experience
 *
 * The banner visibility is controlled by localStorage, allowing it to be permanently dismissed by the user.
 * On initial load, the banner will appear after a 5-second delay. Once dismissed, it won't show again
 * unless the user clears their browser storage.
 *
 * @param props - AppBanner properties
 * @param props.title - Main headline text for the banner (e.g., "Download Our App")
 * @param props.subtitle - Supporting text beneath the title
 * @param props.buttonText - Call-to-action button label (e.g., "Download Now")
 * @param props.link - Optional link configuration for the CTA button
 * @param props.className - Optional CSS class for custom styling
 *
 * @returns A wrapped AppBanner component with link masking and visibility management
 *
 * @example
 * ```tsx
 * <AppBanner
 *   title="Get the Pixum App"
 *   subtitle="Create photo books on the go"
 *   buttonText="Download Now"
 *   link={{
 *     url: 'https://pixum.com/app',
 *     text: 'Download',
 *     title: 'Download Pixum App',
 *     target: '_blank'
 *   }}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Minimal usage without link
 * <AppBanner
 *   title="Download Our App"
 *   subtitle="Best photo products"
 *   buttonText="Get App"
 * />
 * ```
 */
export function AppBanner(props: AppBannerProps) {
  const { link, buttonText, subtitle, title } = props;
  const [isAppBannerVisible, setIsAppBannerVisible] = useState(false);
  const appLink = useAppAdjustLink(link?.url || '', CREATIVE_ID.HEADER);

  useEffect(() => {
    const shouldBeShown = shouldShow();

    const showAppBanner = () => {
      setIsAppBannerVisible(true);
    };

    const timer = setTimeout(() => {
      if (shouldBeShown) {
        showAppBanner();

        // If the user is on scroll position 0 then scroll to top when showing the banner to ensure the header is
        // visible along with the banner. Otherwise the banner will over the header content.
        if (window.scrollY === 0) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  /**
   * Handles banner dismissal (close button).
   * Stores dismissal state in localStorage and hides banner.
   */
  const handleAppBannerClose = () => {
    dismiss();
    setIsAppBannerVisible(false);
  };

  /**
   * Handles tracking when the app banner CTA is clicked.
   * Navigation is handled by withLinkMasking HOC - this only tracks the event.
   * Tracks as Link-Out event to match Gatsby implementation.
   */
  const handleAppBannerClick = () => {
    trackLinkOut(appLink || '');
  };

  return (
    <AppBannerWithLinkMasking
      className={clsx(styles.appBanner)}
      ctaLabel={buttonText}
      href={appLink || ''}
      rating={5}
      subtitle={subtitle}
      title={title}
      onClose={handleAppBannerClose}
      onClick={handleAppBannerClick}
      eventHandlerProcessing="wrap"
      position="sticky"
      show={isAppBannerVisible}
      masked={link?.masked}
      dataLink={appLink || ''}
    />
  );
}
